import * as Sentry from "@sentry/react";

/**
 * Sentry 로 오류를 보낼 때 쓰는 공용 함수.
 *
 * 오류(Error)가 아닌 값 — 서버 오류 응답({ error: { code, status, message } })이나 직접 던진 객체 — 을
 * 그대로 captureException 에 넘기면 원인이 사라진 채 "Error: [object Object]" 로만 찍힌다
 * (2026-10-16 PATHRADE-6F: 실제 값은 프론트가 스스로 막은 중복 요청 { status: "DUPLICATED_REQUEST" } 였다).
 * 그래서 내용을 문구로 풀어 진짜 Error 로 감싸 보내고, 원래 값은 extra 에 남긴다.
 * 같은 종류끼리 한 이슈로 묶이도록 fingerprint 를 상태값(또는 값의 종류)으로 준다.
 */

const MAX_MESSAGE_LENGTH = 500;
// 원래 값을 extra 에 남길 때 가리는 키 — 요청 설정 객체가 섞여 들어와도 토큰이 새지 않게.
const SECRET_KEY = /authorization|token|password|cookie|secret/i;

export const NON_ERROR_NAME = "NonErrorReported";

function truncate(text: string): string {
  return text.length > MAX_MESSAGE_LENGTH
    ? `${text.slice(0, MAX_MESSAGE_LENGTH)}…`
    : text;
}

/** 순환 참조·비밀 키를 견디는 JSON 문자열. 실패하면 String(value). */
export function safeStringify(value: unknown): string {
  try {
    const seen = new WeakSet<object>();
    const text = JSON.stringify(value, (key, current) => {
      if (key && SECRET_KEY.test(key)) {
        return "[가림]";
      }
      if (typeof current === "object" && current !== null) {
        if (seen.has(current)) {
          return "[순환]";
        }
        seen.add(current);
      }
      return current;
    });
    return text ?? String(value);
  } catch {
    return String(value);
  }
}

function errorPayload(value: unknown): Record<string, unknown> | undefined {
  const payload = (value as { error?: unknown } | null | undefined)?.error;
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if ("status" in record || "code" in record) {
      return record;
    }
  }
  return undefined;
}

function kindOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") {
    return (value as object).constructor?.name || "object";
  }
  return typeof value;
}

/** 오류가 아닌 값을 사람이 읽을 한 줄로. */
export function describeNonError(value: unknown): string {
  const payload = errorPayload(value);
  if (payload) {
    const parts = [
      `status=${String(payload.status ?? "-")}`,
      `code=${String(payload.code ?? "-")}`,
    ];
    const message =
      typeof payload.message === "string"
        ? payload.message
        : payload.message === undefined
        ? ""
        : safeStringify(payload.message);
    if (message) {
      parts.push(`message=${message}`);
    }
    return truncate(`ErrorResponse ${parts.join(" ")}`);
  }
  if (typeof value === "string") {
    return truncate(value);
  }
  return truncate(`${kindOf(value)} ${safeStringify(value)}`);
}

/** Sentry 묶음 기준 — 서버 오류 응답은 상태값, 그 밖에는 값의 종류. */
export function fingerprintOf(value: unknown): string[] {
  const payload = errorPayload(value);
  if (payload) {
    return [NON_ERROR_NAME, "ErrorResponse", String(payload.status ?? "-")];
  }
  return [NON_ERROR_NAME, kindOf(value)];
}

/** Sentry 로 보낼 Error. 이미 Error 면 그대로 둔다. */
export function toReportableError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  const error = new Error(describeNonError(value));
  error.name = NON_ERROR_NAME;
  return error;
}

/**
 * 오류를 Sentry 로 보낸다. Error 는 지금처럼 그대로, 그 밖의 값은 내용을 풀어 감싸서.
 * context 는 extra 로 함께 남긴다(어디서 보냈는지 등).
 */
export function reportError(
  value: unknown,
  context?: Record<string, unknown>
): void {
  if (value instanceof Error) {
    Sentry.captureException(value, context ? { extra: context } : undefined);
    return;
  }
  Sentry.captureException(toReportableError(value), {
    fingerprint: fingerprintOf(value),
    extra: { ...context, original: safeStringify(value) },
  });
}
